import React from "react";
import makeStyles from "@mui/styles/makeStyles";
import { Box } from "@mui/material";
import CircleIcon from "@mui/icons-material/Brightness1";
import OutlinedCircleIcon from "@mui/icons-material/Brightness1Outlined";
import PropTypes from "prop-types";
import { ListItem, ListItemText, Divider, Typography } from "@mui/material";
import CalendarIcon from "../Icons/CalendarIcon";
import IconButton from "@mui/material/IconButton";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";

const useStyles = makeStyles(theme => ({
  divider: {
    marginLeft: theme.spacing(7),
    [theme.breakpoints.down("sm")]: {
      marginLeft: theme.spacing(6)
    }
  },
  listItemSectionOne: {
    display: "flex",
    alignItems: "center"
  },
  listItemSectionTwo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexBasis: theme.spacing(15.5),
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
      flexBasis: theme.spacing(13),
      paddingLeft: theme.spacing(1)
    }
  },
  listItem: {
    color: theme.palette.text.secondary,
    minHeight: 48,
    display: "flex",
    justifyContent: "space-between"
  },
  listItemText: {
    textAlign: "end"
  },
  title: {
    marginLeft: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      marginLeft: theme.spacing(2.5)
    }
  }
}));

function ActivityTitle(props) {
  const classes = useStyles();

  return (
    <Typography
      className={classes.title}
      variant="body2"
      color={props.activityComplete ? "textPrimary" : "textSecondary"}
    >
      {props.title}
    </Typography>
  );
}

function CompletionStatusIcon(props) {
  return props.activityComplete ? (
    <CircleIcon color="primary" />
  ) : (
    <OutlinedCircleIcon color="disabled" />
  );
}

function ActivityCalendarButton(props) {
  const { t } = useTranslation();
  return !props.activityComplete ? (
    <Box my={-1}>
      <Tooltip title={t("coursePlayer.addToCalendarTooltip")}>
        <IconButton onClick={props.click} color="primary" size="large">
          {<CalendarIcon />}
        </IconButton>
      </Tooltip>
    </Box>
  ) : null;
}

function ActivityProgress(props) {
  const classes = useStyles();
  const { t } = useTranslation();

  if (props.activityComplete) {
    if (props.completedScore || props.completedScore === 0) {
      return (
        <ListItemText
          className={classes.listItemText}
          primaryTypographyProps={{
            className: "italic",
            variant: "body2",
            color: "textPrimary"
          }}
        >
          {props.completedScore}%
        </ListItemText>
      );
    } else {
      return (
        <ListItemText
          className={classes.listItemText}
          primaryTypographyProps={{
            className: "italic",
            variant: "body2",
            color: "textPrimary"
          }}
        >
          {t("coursePlayer.activityStatus.completed")}
        </ListItemText>
      );
    }
  } else {
    return (
      <ListItemText
        className={classes.listItemText}
        primaryTypographyProps={{
          className: "italic",
          variant: "body2",
          color: "textSecondary"
        }}
      >
        {props.timeEstimate}
      </ListItemText>
    );
  }
}

export default function CourseCardExpansionPanelItem({
  lastItem,
  onAddToCalendarClick,
  completed,
  title,
  timeEstimate,
  completedScore
}) {
  const classes = useStyles();

  return (
    <div>
      <ListItem disableGutters classes={{ root: classes.listItem }}>
        <div className={classes.listItemSectionOne}>
          <CompletionStatusIcon activityComplete={completed} />
          <ActivityTitle activityComplete={completed} title={title} />
        </div>
        <div className={classes.listItemSectionTwo}>
          <ActivityCalendarButton
            activityComplete={completed}
            click={onAddToCalendarClick}
          />
          <ActivityProgress
            activityComplete={completed}
            timeEstimate={timeEstimate}
            completedScore={completedScore}
          />
        </div>
      </ListItem>
      {!lastItem && <Divider classes={{ root: classes.divider }} />}
    </div>
  );
}

CourseCardExpansionPanelItem.propTypes = {
  completed: PropTypes.bool,
  completedScore: PropTypes.number,
  timeEstimate: PropTypes.string,
  name: PropTypes.string,
  calendars: PropTypes.arrayOf(PropTypes.object),
  lastItem: PropTypes.bool
};
